import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Alert,
  AlertTitle,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Stack,
  IconButton,
  CardHeader
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DescriptionIcon from "@mui/icons-material/Description";
import LanguageIcon from "@mui/icons-material/Language";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import axios from "axios";
import { useAuth } from "../services/auth.service";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Helper to format currency
const formatMoney = (value, currency = "USD") => {
  const num = typeof value === "number" ? value : Number(value ?? 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(num) ? num : 0);
};

// Helper to format Date-time safely
const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("es-PA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Mask sensitive PII data to fulfill secure coding standards
const maskAccountNumber = (acc) => {
  if (!acc || acc.length < 4) return "****";
  return `${acc.slice(0, 3)}****${acc.slice(-3)}`;
};

const maskEmail = (email) => {
  if (!email || !email.includes("@")) return "***@***.com";
  const [name, domain] = email.split("@");
  return `${name.charAt(0)}***@${domain}`;
};

// Badge rendering for transaction execution status
const getStatusChip = (code, desc) => {
  const c = String(code || "").trim();
  const label = desc || (c === "0000" ? "Completado" : "Pendiente");

  const common = {
    size: "small",
    variant: "filled",
    sx: {
      height: 22,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      borderRadius: 999,
    },
  };

  if (c === "0000") {
    return (
      <Chip
        {...common}
        label={label}
        sx={{ ...common.sx, bgcolor: "#d1e7dd", color: "#0f5132" }}
      />
    );
  }
  if (c === "9999" || c === "ERR" || c === "FAIL") {
    return (
      <Chip
        {...common}
        label={label}
        sx={{ ...common.sx, bgcolor: "#f8d7da", color: "#842029" }}
      />
    );
  }
  return (
    <Chip
      {...common}
      label={label}
      sx={{ ...common.sx, bgcolor: "#fff3cd", color: "#664d03" }}
    />
  );
};

// Component styling configuration
const PRIMARY_BLUE = "#002B49";
const CYAN_ACCENT = "#00A3E0";

export default function ConexionBg() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = React.useState(0);

  // Notifications state
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success"
  });

  const triggerNotification = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Shared Axios configuration
  const getAxiosConfig = () => {
    const token = user?.accessToken || "";
    return {
      headers: {
        "Content-Type": "application/json",
        "accept": "application/json",
        "x-access-token": token,
        "Authorization": `Bearer ${token}`
      }
    };
  };

  // ----------------------------------------------------
  // TAB 0: ESTADO DE CUENTA
  // ----------------------------------------------------
  const [statementParams, setStatementParams] = React.useState({
    CodProducto: 3, // 3 = checking, 4 = savings
    NumCuenta: "0300123456",
    FechaDesde: dayjs().subtract(7, "day"),
    FechaHasta: dayjs(),
    Orden: "D", // D = descending, A = ascending
    UseReferences: false
  });

  const [statementLoading, setStatementLoading] = React.useState(false);
  const [movements, setMovements] = React.useState([]);
  const [statementMeta, setStatementMeta] = React.useState({
    balance: 0,
    totalCredits: 0,
    totalDebits: 0
  });

  const fetchStatement = async () => {
    setStatementLoading(true);
    setMovements([]);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_API || "http://localhost:5000/api";
      const endpoint = statementParams.UseReferences 
        ? `${apiBase}/conexion-bg/h2h/cuentas/referencias`
        : `${apiBase}/conexion-bg/h2h/cuentas`;

      const params = new URLSearchParams({
        CodProducto: statementParams.CodProducto,
        NumCuenta: statementParams.NumCuenta,
        FechaDesde: statementParams.FechaDesde.format("YYYY-MM-DD") + "T00:00:00",
        FechaHasta: statementParams.FechaHasta.format("YYYY-MM-DD") + "T23:59:59",
        Orden: statementParams.Orden,
        CantidadRegistros: 50,
        UltimoRegistro: 0
      });

      const response = await axios.get(`${endpoint}?${params.toString()}`, getAxiosConfig());
      
      if (response.data) {
        const moves = response.data.movimientosH2H || [];
        setMovements(moves);
        
        // Calculate dynamic balances for demo metrics
        let credits = 0;
        let debits = 0;
        moves.forEach(m => {
          const mAmount = Number(m.monto || 0);
          if (m.naturalezaMovimiento === "C" || m.tipoMovimiento === "CREDIT" || mAmount > 0) {
            credits += mAmount;
          } else {
            debits += Math.abs(mAmount);
          }
        });

        // Set static metadata
        setStatementMeta({
          balance: moves[0]?.saldoCapital || 12450.75, // Fallback if mock is missing
          totalCredits: credits,
          totalDebits: debits
        });

        triggerNotification("Movimientos cargados con éxito", "success");
      }
    } catch (err) {
      console.error("Error retrieving account statement:", err.message);
      triggerNotification("No se pudieron cargar los movimientos de la cuenta", "error");
    } finally {
      setStatementLoading(false);
    }
  };

  // ----------------------------------------------------
  // TAB 1: TRANSFERENCIAS INDIVIDUALES
  // ----------------------------------------------------
  const [indForm, setIndForm] = React.useState({
    cuentaOrigen: "0300123456",
    codigoProducto: 3,
    nombreBeneficiario: "",
    numeroCuentaBeneficiario: "",
    codigoProductoBeneficiario: 3,
    codigoBanco: 1, // Banco General
    monto: "",
    correo: "",
    descripcion: "",
    trnPropia: false
  });

  const [indLoading, setIndLoading] = React.useState(false);
  const [indResult, setIndResult] = React.useState(null);
  
  // Status check variables
  const [indCheckParams, setIndCheckParams] = React.useState({
    fecha: dayjs(),
    codigoAutorizacion: ""
  });
  const [indCheckLoading, setIndCheckLoading] = React.useState(false);
  const [indCheckResults, setIndCheckResults] = React.useState([]);

  const submitIndividualPayment = async (e) => {
    e.preventDefault();
    if (!indForm.nombreBeneficiario || !indForm.numeroCuentaBeneficiario || !indForm.monto) {
      triggerNotification("Complete todos los campos obligatorios", "warning");
      return;
    }

    setIndLoading(true);
    setIndResult(null);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_API || "http://localhost:5000/api";
      const payload = {
        transacciones: [
          {
            secuencial: 1,
            descripcion: indForm.descripcion,
            monto: Number(indForm.monto),
            trnPropia: indForm.trnPropia,
            codigoProducto: Number(indForm.codigoProducto),
            cuentaOrigen: indForm.cuentaOrigen,
            correo: indForm.correo,
            nombreBeneficiario: indForm.nombreBeneficiario.toUpperCase(),
            codigoProductoBeneficiario: Number(indForm.codigoProductoBeneficiario),
            numeroCuentaBeneficiario: indForm.numeroCuentaBeneficiario,
            codigoBanco: Number(indForm.codigoBanco)
          }
        ]
      };

      const response = await axios.post(
        `${apiBase}/conexion-bg/h2h/transaccion/individuales`, 
        payload, 
        getAxiosConfig()
      );

      if (response.data) {
        setIndResult(response.data);
        const code = response.data.status?.returnStatus?.returnCode;
        if (code === "0000") {
          triggerNotification("Transferencia enviada con éxito", "success");
        } else {
          triggerNotification("Transferencia rechazada: " + response.data.status?.returnStatus?.returnCodeDesc, "error");
        }
      }
    } catch (err) {
      console.error("Error executing individual transfer:", err.message);
      triggerNotification("Error al procesar la transferencia bancaria", "error");
    } finally {
      setIndLoading(false);
    }
  };

  const checkIndividualPayments = async () => {
    setIndCheckLoading(true);
    setIndCheckResults([]);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_API || "http://localhost:5000/api";
      const params = new URLSearchParams({
        fecha: indCheckParams.fecha.format("YYYY-MM-DD") + "T12:00:00"
      });
      if (indCheckParams.codigoAutorizacion) {
        params.append("codigoAutorizacion", indCheckParams.codigoAutorizacion);
      }

      const response = await axios.get(
        `${apiBase}/conexion-bg/h2h/transaccion/individuales?${params.toString()}`, 
        getAxiosConfig()
      );

      if (response.data) {
        setIndCheckResults(response.data.body?.detalleTransaccionesIndividuales || []);
        triggerNotification("Estado consultado exitosamente", "success");
      }
    } catch (err) {
      console.error("Error querying individual transfers:", err.message);
      triggerNotification("No se pudo consultar el estado de las transferencias", "error");
    } finally {
      setIndCheckLoading(false);
    }
  };

  // ----------------------------------------------------
  // TAB 2: TRANSACCIONES MASIVAS (LOTES ACH)
  // ----------------------------------------------------
  const [achHeader, setAchHeader] = React.useState({
    nombreTitular: "GENESIS ASESORES DE SEGUROS S.A.",
    totalMonto: 0,
    cantidadRegistros: 0
  });

  const [achDetails, setAchDetails] = React.useState([
    { secuenciaDetalle: "1", monto: "", nombreBeneficiario: "", numeroCuentaBeneficiario: "" }
  ]);

  const [achLoading, setAchLoading] = React.useState(false);
  const [achResult, setAchResult] = React.useState(null);

  // Status check for ACH batch
  const [achCheckCode, setAchCheckCode] = React.useState("");
  const [achCheckLoading, setAchCheckLoading] = React.useState(false);
  const [achCheckData, setAchCheckData] = React.useState(null);

  const addAchRow = () => {
    setAchDetails([
      ...achDetails,
      { secuenciaDetalle: String(achDetails.length + 1), monto: "", nombreBeneficiario: "", numeroCuentaBeneficiario: "" }
    ]);
  };

  const removeAchRow = (index) => {
    if (achDetails.length <= 1) return;
    const updated = achDetails.filter((_, i) => i !== index).map((row, idx) => ({
      ...row,
      secuenciaDetalle: String(idx + 1)
    }));
    setAchDetails(updated);
  };

  const handleAchDetailChange = (index, field, value) => {
    const updated = [...achDetails];
    updated[index][field] = value;
    setAchDetails(updated);

    // Dynamic calculations for the header
    let totalVal = 0;
    let validCount = 0;
    updated.forEach(row => {
      const parsed = Number(row.monto);
      if (!isNaN(parsed) && parsed > 0) {
        totalVal += parsed;
        validCount++;
      }
    });

    setAchHeader({
      ...achHeader,
      totalMonto: Math.round(totalVal * 100) / 100,
      cantidadRegistros: validCount
    });
  };

  const submitAchBatch = async () => {
    if (achHeader.cantidadRegistros === 0) {
      triggerNotification("Debe ingresar al menos una transacción válida con monto mayor a 0", "warning");
      return;
    }

    setAchLoading(true);
    setAchResult(null);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_API || "http://localhost:5000/api";
      const payload = {
        cabeceraTransaccion: {
          nombreTitular: achHeader.nombreTitular,
          totalMonto: achHeader.totalMonto,
          cantidadRegistros: achHeader.cantidadRegistros
        },
        detalleLoteTransaccion: achDetails
          .filter(d => Number(d.monto) > 0 && d.nombreBeneficiario && d.numeroCuentaBeneficiario)
          .map(d => ({
            secuenciaDetalle: d.secuenciaDetalle,
            monto: Number(d.monto),
            nombreBeneficiario: d.nombreBeneficiario.toUpperCase(),
            numeroCuentaBeneficiario: d.numeroCuentaBeneficiario
          }))
      };

      const response = await axios.post(
        `${apiBase}/conexion-bg/h2h/transacciones`, 
        payload, 
        getAxiosConfig()
      );

      if (response.data) {
        setAchResult(response.data);
        if (response.data.status?.returnStatus?.returnCode === "0000") {
          triggerNotification("Lote ACH enviado con éxito", "success");
        } else {
          triggerNotification("Lote ACH rechazado: " + response.data.status?.returnStatus?.returnCodeDesc, "error");
        }
      }
    } catch (err) {
      console.error("Error submitting ACH Batch:", err.message);
      triggerNotification("Error al enviar el lote transaccional", "error");
    } finally {
      setAchLoading(false);
    }
  };

  const checkAchStatus = async () => {
    if (!achCheckCode) {
      triggerNotification("Ingrese el código de pago del lote", "warning");
      return;
    }

    setAchCheckLoading(true);
    setAchCheckData(null);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_API || "http://localhost:5000/api";
      const params = new URLSearchParams({
        codigoPago: achCheckCode,
        numeroRegistros: 50,
        ultimoRegistro: 0
      });

      const response = await axios.get(
        `${apiBase}/conexion-bg/h2h/transacciones?${params.toString()}`, 
        getAxiosConfig()
      );

      if (response.data) {
        setAchCheckData(response.data.body || null);
        if (response.data.status?.returnStatus?.returnCode === "0000") {
          triggerNotification("Estado del lote ACH recuperado", "success");
        } else {
          triggerNotification("Lote reporta un error: " + response.data.status?.returnStatus?.returnCodeDesc, "warning");
        }
      }
    } catch (err) {
      console.error("Error checking ACH status:", err.message);
      triggerNotification("No se pudo consultar el estado del lote", "error");
    } finally {
      setAchCheckLoading(false);
    }
  };

  // ----------------------------------------------------
  // TAB 3: REMESAS & CONFIRMING
  // ----------------------------------------------------
  const [remesaForm, setRemesaForm] = React.useState({
    codigoProveedor: "",
    invoiceNumber: "",
    amount: ""
  });
  const [remesaLoading, setRemesaLoading] = React.useState(false);
  const [remesaResult, setRemesaResult] = React.useState(null);

  const [remesaCheckCode, setRemesaCheckCode] = React.useState("");
  const [remesaCheckLoading, setRemesaCheckLoading] = React.useState(false);
  const [remesaCheckStatus, setRemesaCheckStatus] = React.useState(null);

  const submitConfirmingRemesa = async (e) => {
    e.preventDefault();
    if (!remesaForm.codigoProveedor || !remesaForm.invoiceNumber || !remesaForm.amount) {
      triggerNotification("Complete todos los campos obligatorios", "warning");
      return;
    }

    setRemesaLoading(true);
    setRemesaResult(null);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_API || "http://localhost:5000/api";
      const payload = {
        codigoProveedor: remesaForm.codigoProveedor,
        remesas: [
          {
            factura: remesaForm.invoiceNumber,
            monto: Number(remesaForm.amount),
            fechaFactura: dayjs().format("YYYY-MM-DD")
          }
        ]
      };

      const response = await axios.post(
        `${apiBase}/conexion-bg/h2h-confirming/remesa`,
        payload,
        getAxiosConfig()
      );

      if (response.data) {
        setRemesaResult(response.data);
        triggerNotification("Remesa de Confirming creada con éxito", "success");
      }
    } catch (err) {
      console.error("Error creating Confirming Remittance:", err.message);
      triggerNotification("Error al enviar remesa de Confirming", "error");
    } finally {
      setRemesaLoading(false);
    }
  };

  const checkRemesaStatus = async () => {
    if (!remesaCheckCode) {
      triggerNotification("Ingrese el código de remesa", "warning");
      return;
    }

    setRemesaCheckLoading(true);
    setRemesaCheckStatus(null);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_API || "http://localhost:5000/api";
      const params = new URLSearchParams({
        codigoRemesa: remesaCheckCode
      });

      const response = await axios.get(
        `${apiBase}/conexion-bg/h2h-confirming/remesa?${params.toString()}`,
        getAxiosConfig()
      );

      if (response.data) {
        setRemesaCheckStatus(response.data.estadoRemesa || "ESTADO NO RECUPERADO");
        triggerNotification("Estado de remesa cargado", "success");
      }
    } catch (err) {
      console.error("Error retrieving remesa status:", err.message);
      triggerNotification("No se pudo obtener el estado de la remesa", "error");
    } finally {
      setRemesaCheckLoading(false);
    }
  };

  // ----------------------------------------------------
  // TAB 4: TRANSFERENCIAS INTERNACIONALES
  // ----------------------------------------------------
  const [intlForm, setIntlForm] = React.useState({
    beneficiaryName: "",
    beneficiaryBank: "",
    swiftCode: "",
    amount: "",
    currency: "USD",
    accountNumber: "",
    details: ""
  });
  const [intlLoading, setIntlLoading] = React.useState(false);
  const [intlResult, setIntlResult] = React.useState(null);

  const [intlCheckCode, setIntlCheckCode] = React.useState("");
  const [intlCheckLoading, setIntlCheckLoading] = React.useState(false);
  const [intlCheckData, setIntlCheckData] = React.useState(null);

  const submitIntlPayment = async (e) => {
    e.preventDefault();
    if (!intlForm.beneficiaryName || !intlForm.swiftCode || !intlForm.amount) {
      triggerNotification("Complete los campos obligatorios", "warning");
      return;
    }

    setIntlLoading(true);
    setIntlResult(null);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_API || "http://localhost:5000/api";
      const payload = {
        DetalleTransferenciaInternacional: [
          {
            secuencial: 1,
            nombreBeneficiario: intlForm.beneficiaryName.toUpperCase(),
            bancoBeneficiario: intlForm.beneficiaryBank,
            codigoSwift: intlForm.swiftCode,
            monto: Number(intlForm.amount),
            moneda: intlForm.currency,
            cuentaBeneficiario: intlForm.accountNumber,
            descripcion: intlForm.details
          }
        ]
      };

      const response = await axios.post(
        `${apiBase}/conexion-bg/h2h/transaccion/internacional`,
        payload,
        getAxiosConfig()
      );

      if (response.data) {
        setIntlResult(response.data);
        if (response.data.status?.returnStatus?.returnCode === "0000") {
          triggerNotification("Transferencia internacional enviada", "success");
        } else {
          triggerNotification("Rechazada: " + response.data.status?.returnStatus?.returnCodeDesc, "error");
        }
      }
    } catch (err) {
      console.error("Error creating international wire transfer:", err.message);
      triggerNotification("Error al enviar transferencia internacional", "error");
    } finally {
      setIntlLoading(false);
    }
  };

  const checkIntlStatus = async () => {
    if (!intlCheckCode) {
      triggerNotification("Ingrese el código de pago", "warning");
      return;
    }

    setIntlCheckLoading(true);
    setIntlCheckData(null);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_API || "http://localhost:5000/api";
      const params = new URLSearchParams({
        codigoPago: intlCheckCode,
        fecha: dayjs().format("YYYY-MM-DD") + "T12:00:00"
      });

      const response = await axios.get(
        `${apiBase}/conexion-bg/h2h/transaccion/internacional?${params.toString()}`,
        getAxiosConfig()
      );

      if (response.data) {
        setIntlCheckData(response.data || null);
        triggerNotification("Detalles internacionales recuperados", "success");
      }
    } catch (err) {
      console.error("Error checking intl wire status:", err.message);
      triggerNotification("No se pudo obtener el estado de la transferencia internacional", "error");
    } finally {
      setIntlCheckLoading(false);
    }
  };

  // Initial load
  React.useEffect(() => {
    fetchStatement();
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, maxWidth: "1280px", margin: "0 auto", color: "#111b21" }}>
        
        {/* Modern Corporate Header */}
        <Box 
          sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            mb: 3, 
            pb: 2, 
            borderBottom: `2px solid ${PRIMARY_BLUE}` 
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box 
              sx={{ 
                bgcolor: PRIMARY_BLUE, 
                color: "#fff", 
                p: 1.5, 
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <AccountBalanceIcon fontSize="large" />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: PRIMARY_BLUE, m: 0 }}>
                Conexión BG H2H
              </Typography>
              <Typography variant="body2" sx={{ color: "#54656f", fontWeight: 500 }}>
                Gateway de Integración Directa con Banco General (Host-to-Host)
              </Typography>
            </Box>
          </Box>
          <Chip 
            label="ADMIN CONSOLE" 
            sx={{ 
              bgcolor: `${PRIMARY_BLUE}15`, 
              color: PRIMARY_BLUE, 
              fontWeight: 700, 
              fontSize: 12, 
              border: `1px solid ${PRIMARY_BLUE}`
            }} 
          />
        </Box>

        {/* Sandbox alert banner detailing mock setup (as per README requirements) */}
        <Alert 
          severity="info" 
          icon={<InfoIcon sx={{ color: CYAN_ACCENT }} />}
          sx={{ 
            mb: 3, 
            borderLeft: `5px solid ${CYAN_ACCENT}`,
            backgroundColor: "#e8f4fd",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
            borderRadius: "8px"
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, color: PRIMARY_BLUE }}>Entorno Sandbox Automatizado</AlertTitle>
          Actualmente opera bajo <strong>Sandbox Integrado</strong>. Las transacciones y consultas devolverán datos simulados si la conexión a IBM Cloud Gateway no dispone de credenciales en el archivo <code>.env</code>. Para operaciones reales, modifique <code>CONEXION_BG_SANDBOX=false</code> y configure el token de IBM API Connect.
        </Alert>

        {/* Navigation Tabs with Pro UX Styling */}
        <Paper 
          elevation={1} 
          sx={{ 
            borderRadius: "12px", 
            mb: 3, 
            overflow: "hidden", 
            border: "1px solid #e1e9ee" 
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newTab) => setActiveTab(newTab)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              backgroundColor: "#f8f9fa",
              "& .MuiTabs-indicator": {
                backgroundColor: CYAN_ACCENT,
                height: "3px"
              },
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: "14px",
                py: 2,
                color: "#54656f",
                "&.Mui-selected": {
                  color: PRIMARY_BLUE
                }
              }
            }}
          >
            <Tab icon={<HistoryIcon sx={{ fontSize: 20 }} />} label="Cuentas y Movimientos" iconPosition="start" />
            <Tab icon={<SendIcon sx={{ fontSize: 20 }} />} label="Transferencia Individual" iconPosition="start" />
            <Tab icon={<DescriptionIcon sx={{ fontSize: 20 }} />} label="Lotes ACH (Masivo)" iconPosition="start" />
            <Tab icon={<ReceiptIcon sx={{ fontSize: 20 }} />} label="Confirming / Remesas" iconPosition="start" />
            <Tab icon={<LanguageIcon sx={{ fontSize: 20 }} />} label="Transf. Internacional" iconPosition="start" />
          </Tabs>
        </Paper>

        {/* ---------------------------------------------------- */}
        {/* PANEL 0: CUENTAS Y MOVIMIENTOS */}
        {/* ---------------------------------------------------- */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            
            {/* Account Selector & Bank Card UI */}
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  borderRadius: "16px", 
                  background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, #0c3e66 100%)`, 
                  color: "#fff",
                  boxShadow: "0px 4px 20px rgba(0, 43, 73, 0.25)",
                  mb: 3,
                  position: "relative",
                  overflow: "hidden"
                }}
              >
                {/* Visual Accent */}
                <Box 
                  sx={{ 
                    position: "absolute", 
                    width: "150px", 
                    height: "150px", 
                    borderRadius: "50%", 
                    background: `${CYAN_ACCENT}15`, 
                    top: "-50px", 
                    right: "-50px" 
                  }} 
                />

                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4 }}>
                    <Box>
                      <Typography sx={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: 1.5, opacity: 0.8 }}>
                        Banco General H2H
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {statementParams.CodProducto === 3 ? "Cuenta Corriente" : "Cuenta de Ahorros"}
                      </Typography>
                    </Box>
                    <CreditCardIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>

                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, fontFamily: "monospace", letterSpacing: 1 }}>
                    {formatMoney(statementMeta.balance)}
                  </Typography>

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <Box>
                      <Typography sx={{ fontSize: "9px", textTransform: "uppercase", opacity: 0.6 }}>
                        Número de Cuenta
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
                        {maskAccountNumber(statementParams.NumCuenta)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontSize: "9px", textTransform: "uppercase", opacity: 0.6 }}>
                        Titular
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        GENESIS ASESORES
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Statement Parameters Form */}
              <Card sx={{ borderRadius: "12px", border: "1px solid #e1e9ee" }}>
                <CardHeader title="Filtros de Búsqueda" titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }} />
                <Divider />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Cuenta</InputLabel>
                    <Select
                      value={statementParams.CodProducto}
                      label="Tipo de Cuenta"
                      onChange={(e) => setStatementParams({ ...statementParams, CodProducto: e.target.value })}
                    >
                      <MenuItem value={3}>Cuenta Corriente (03)</MenuItem>
                      <MenuItem value={4}>Cuenta de Ahorros (04)</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Número de Cuenta"
                    variant="outlined"
                    size="small"
                    value={statementParams.NumCuenta}
                    onChange={(e) => setStatementParams({ ...statementParams, NumCuenta: e.target.value })}
                  />

                  <DatePicker
                    label="Fecha Desde"
                    value={statementParams.FechaDesde}
                    onChange={(val) => setStatementParams({ ...statementParams, FechaDesde: val })}
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                  />

                  <DatePicker
                    label="Fecha Hasta"
                    value={statementParams.FechaHasta}
                    onChange={(val) => setStatementParams({ ...statementParams, FechaHasta: val })}
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                  />

                  <FormControl fullWidth size="small">
                    <InputLabel>Orden de Movimientos</InputLabel>
                    <Select
                      value={statementParams.Orden}
                      label="Orden de Movimientos"
                      onChange={(e) => setStatementParams({ ...statementParams, Orden: e.target.value })}
                    >
                      <MenuItem value="D">Más Recientes Primero (Desc.)</MenuItem>
                      <MenuItem value="A">Más Antiguos Primero (Asc.)</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={statementParams.UseReferences} 
                        onChange={(e) => setStatementParams({ ...statementParams, UseReferences: e.target.checked })} 
                      />
                    }
                    label="Consultar Movimientos con Referenciación"
                  />

                  <Button
                    variant="contained"
                    onClick={fetchStatement}
                    disabled={statementLoading}
                    startIcon={statementLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    sx={{ 
                      bgcolor: PRIMARY_BLUE,
                      "&:hover": { bgcolor: "#0c3e66" },
                      fontWeight: 600,
                      py: 1
                    }}
                  >
                    {statementLoading ? "Buscando..." : "Consultar H2H"}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Statistics and Details Table */}
            <Grid item xs={12} md={8}>
              {/* Financial KPI Dashboard */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px", border: "1px solid #e1e9ee" }}>
                    <Typography variant="caption" sx={{ color: "#54656f", fontWeight: 600, uppercase: true }}>
                      Total Créditos (Entradas)
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#198754", mt: 0.5 }}>
                      {formatMoney(statementMeta.totalCredits)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px", border: "1px solid #e1e9ee" }}>
                    <Typography variant="caption" sx={{ color: "#54656f", fontWeight: 600, uppercase: true }}>
                      Total Débitos (Salidas)
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#dc3545", mt: 0.5 }}>
                      {formatMoney(statementMeta.totalDebits)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Transactions List */}
              <Paper variant="outlined" sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e1e9ee" }}>
                <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f8f9fa" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY_BLUE }}>
                    Historial de Transacciones H2H
                  </Typography>
                  <IconButton size="small" onClick={fetchStatement} disabled={statementLoading}>
                    <RefreshIcon />
                  </IconButton>
                </Box>
                <Divider />
                
                {statementLoading ? (
                  <Box sx={{ p: 6, textAlignment: "center", display: "flex", justifyContent: "center" }}>
                    <CircularProgress color="primary" />
                  </Box>
                ) : movements.length === 0 ? (
                  <Box sx={{ p: 6, textAlign: "center" }}>
                    <Typography sx={{ color: "#54656f", fontStyle: "italic" }}>
                      No se encontraron movimientos para el rango seleccionado.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Referencia</TableCell>
                          {statementParams.UseReferences && (
                            <TableCell sx={{ fontWeight: 700 }}>Ref. Ampliadas</TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 700 }} align="right">Monto</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Saldo Capital</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {movements.map((move, index) => {
                          const isCredit = move.naturalezaMovimiento === "C" || move.tipoMovimiento === "CREDIT" || move.monto > 0;
                          return (
                            <TableRow key={move.idMovimiento || index} hover>
                              <TableCell>{formatDateTime(move.fecha)}</TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {move.descripcion || "Transacción H2H"}
                              </TableCell>
                              <TableCell>{move.referencia || "—"}</TableCell>
                              {statementParams.UseReferences && (
                                <TableCell>
                                  <Typography variant="caption" display="block">
                                    {[move.referencia1, move.referencia2].filter(Boolean).join(" | ") || "—"}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell align="right" sx={{ fontWeight: 700, color: isCredit ? "#198754" : "#dc3545" }}>
                                {isCredit ? "+" : ""}{formatMoney(move.monto)}
                              </TableCell>
                              <TableCell align="right">{formatMoney(move.saldoCapital)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* ---------------------------------------------------- */}
        {/* PANEL 1: TRANSFERENCIA INDIVIDUAL */}
        {/* ---------------------------------------------------- */}
        {activeTab === 1 && (
          <Grid container spacing={3}>
            
            {/* Payment form slip */}
            <Grid item xs={12} md={7}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e1e9ee", boxShadow: "0px 2px 8px rgba(0,0,0,0.02)" }}>
                <CardHeader 
                  title="Nueva Transferencia" 
                  titleTypographyProps={{ variant: "h6", fontWeight: 700, color: PRIMARY_BLUE }}
                  subheader="Complete el formulario de envío (Cuentas propias o de terceros)"
                />
                <Divider />
                <CardContent component="form" onSubmit={submitIndividualPayment}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Cuenta Origen</InputLabel>
                        <Select
                          value={indForm.cuentaOrigen}
                          label="Cuenta Origen"
                          onChange={(e) => setIndForm({ ...indForm, cuentaOrigen: e.target.value })}
                        >
                          <MenuItem value="0300123456">03-00-123456 (Checking)</MenuItem>
                          <MenuItem value="0400987654">04-00-987654 (Savings)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Producto Origen</InputLabel>
                        <Select
                          value={indForm.codigoProducto}
                          label="Producto Origen"
                          onChange={(e) => setIndForm({ ...indForm, codigoProducto: e.target.value })}
                        >
                          <MenuItem value={3}>Cuenta Corriente</MenuItem>
                          <MenuItem value={4}>Cuenta de Ahorros</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}><Chip label="Datos del Beneficiario" size="small" /></Divider>
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Nombre Completo"
                        size="small"
                        required
                        value={indForm.nombreBeneficiario}
                        onChange={(e) => setIndForm({ ...indForm, nombreBeneficiario: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Banco Destino</InputLabel>
                        <Select
                          value={indForm.codigoBanco}
                          label="Banco Destino"
                          onChange={(e) => setIndForm({ ...indForm, codigoBanco: e.target.value })}
                        >
                          <MenuItem value={1}>Banco General</MenuItem>
                          <MenuItem value={15}>BAC Credomatic</MenuItem>
                          <MenuItem value={32}>Banco Nacional</MenuItem>
                          <MenuItem value={8}>Banistmo</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Número de Cuenta Destino"
                        size="small"
                        required
                        value={indForm.numeroCuentaBeneficiario}
                        onChange={(e) => setIndForm({ ...indForm, numeroCuentaBeneficiario: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Producto Destino</InputLabel>
                        <Select
                          value={indForm.codigoProductoBeneficiario}
                          label="Producto Destino"
                          onChange={(e) => setIndForm({ ...indForm, codigoProductoBeneficiario: e.target.value })}
                        >
                          <MenuItem value={3}>Cuenta Corriente</MenuItem>
                          <MenuItem value={4}>Cuenta de Ahorros</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Monto ($)"
                        type="number"
                        size="small"
                        required
                        value={indForm.monto}
                        onChange={(e) => setIndForm({ ...indForm, monto: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Correo Electrónico (Notificación)"
                        type="email"
                        size="small"
                        value={indForm.correo}
                        onChange={(e) => setIndForm({ ...indForm, correo: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Descripción / Concepto de Pago"
                        size="small"
                        value={indForm.descripcion}
                        onChange={(e) => setIndForm({ ...indForm, descripcion: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={indForm.trnPropia} 
                            onChange={(e) => setIndForm({ ...indForm, trnPropia: e.target.checked })} 
                          />
                        }
                        label="Es Transferencia Entre Cuentas Propias"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={indLoading}
                        startIcon={indLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        sx={{ 
                          bgcolor: PRIMARY_BLUE,
                          "&:hover": { bgcolor: "#0c3e66" },
                          py: 1,
                          fontWeight: 700
                        }}
                      >
                        {indLoading ? "Procesando..." : "Ejecutar Transferencia Directa"}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Output Result Alert Panel */}
              {indResult && (
                <Card sx={{ mt: 3, border: "1px solid #e1e9ee", borderRadius: "12px" }}>
                  <CardHeader title="Resultado de Operación" titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }} />
                  <Divider />
                  <CardContent>
                    {indResult.status?.returnStatus?.returnCode === "0000" ? (
                      <Alert severity="success" icon={<CheckCircleIcon />}>
                        <AlertTitle>Transacción Exitosa</AlertTitle>
                        La transferencia ha sido enviada al banco para su procesamiento.
                        <Box sx={{ mt: 2, fontFamily: "monospace" }}>
                          {indResult.body?.resultadosTransaccion?.map((res, index) => (
                            <div key={index}>
                              <strong>Código Pago:</strong> {res.codigoPago} <br />
                              <strong>Secuencial:</strong> {res.secuencial} <br />
                              <strong>Estado:</strong> {res.estadoTransaccion}
                            </div>
                          ))}
                        </Box>
                      </Alert>
                    ) : (
                      <Alert severity="error" icon={<ErrorIcon />}>
                        <AlertTitle>Operación Rechazada por Banco General</AlertTitle>
                        <strong>Código Error:</strong> {indResult.status?.returnStatus?.returnCode} <br />
                        <strong>Descripción:</strong> {indResult.status?.returnStatus?.returnCodeDesc}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}
            </Grid>

            {/* Inquiry/Query Panel */}
            <Grid item xs={12} md={5}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e1e9ee", mb: 3 }}>
                <CardHeader 
                  title="Consultar Estado" 
                  titleTypographyProps={{ variant: "subtitle1", fontWeight: 700, color: PRIMARY_BLUE }}
                  subheader="Verifique transacciones individuales previas"
                />
                <Divider />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <DatePicker
                    label="Fecha de la Transferencia"
                    value={indCheckParams.fecha}
                    onChange={(val) => setIndCheckParams({ ...indCheckParams, fecha: val })}
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                  />

                  <TextField
                    label="Código de Autorización (Opcional)"
                    size="small"
                    value={indCheckParams.codigoAutorizacion}
                    onChange={(e) => setIndCheckParams({ ...indCheckParams, codigoAutorizacion: e.target.value })}
                  />

                  <Button
                    variant="outlined"
                    onClick={checkIndividualPayments}
                    disabled={indCheckLoading}
                    startIcon={indCheckLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    sx={{
                      borderColor: PRIMARY_BLUE,
                      color: PRIMARY_BLUE,
                      "&:hover": { borderColor: "#0c3e66" }
                    }}
                  >
                    {indCheckLoading ? "Buscando..." : "Consultar Historial"}
                  </Button>
                </CardContent>
              </Card>

              {/* Inquiry Results */}
              <Paper variant="outlined" sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e1e9ee" }}>
                <Box sx={{ p: 2, bgcolor: "#f8f9fa" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY_BLUE }}>
                    Resultados Consulta de Estado
                  </Typography>
                </Box>
                <Divider />
                {indCheckLoading ? (
                  <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : indCheckResults.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="body2" sx={{ color: "#54656f", fontStyle: "italic" }}>
                      No se han realizado consultas o no hay registros para esta fecha.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Sec.</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Beneficiario</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Monto</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="center">Estado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {indCheckResults.map((tx, idx) => (
                          <TableRow key={tx.secuencial || idx} hover>
                            <TableCell>{tx.secuencial}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {tx.nombreBeneficiario || "—"}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" display="block">
                                {maskAccountNumber(tx.numeroCuentaBeneficiario)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {formatMoney(tx.monto)}
                            </TableCell>
                            <TableCell align="center">
                              {getStatusChip(tx.codigoError === "0" ? "0000" : "9999", tx.descError || "Completada")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* ---------------------------------------------------- */}
        {/* PANEL 2: LOTES ACH MASIVOS */}
        {/* ---------------------------------------------------- */}
        {activeTab === 2 && (
          <Grid container spacing={3}>
            
            {/* Batch Composer Form */}
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e1e9ee", mb: 3 }}>
                <CardHeader 
                  title="Creador de Lotes ACH" 
                  titleTypographyProps={{ variant: "h6", fontWeight: 700, color: PRIMARY_BLUE }}
                  subheader="Agregue registros y envíe una transferencia masiva en un solo llamado"
                />
                <Divider />
                <CardContent>
                  
                  {/* Batch Header Params */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nombre del Titular (Cuenta Origen)"
                        size="small"
                        value={achHeader.nombreTitular}
                        onChange={(e) => setAchHeader({ ...achHeader, nombreTitular: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Monto Total Lote"
                        size="small"
                        disabled
                        value={formatMoney(achHeader.totalMonto)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Cantidad Registros"
                        size="small"
                        disabled
                        value={achHeader.cantidadRegistros}
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY_BLUE }}>
                    Detalle del Lote ACH
                  </Typography>

                  {/* List of rows */}
                  <Stack spacing={2} sx={{ mb: 3 }}>
                    {achDetails.map((row, index) => (
                      <Paper 
                        key={index} 
                        variant="outlined" 
                        sx={{ 
                          p: 2, 
                          borderRadius: "8px", 
                          position: "relative",
                          border: "1px solid #e1e9ee"
                        }}
                      >
                        <Box sx={{ position: "absolute", top: 10, right: 10 }}>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => removeAchRow(index)}
                            disabled={achDetails.length <= 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={1} sm={0.5} sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: CYAN_ACCENT }}>
                              #{row.secuenciaDetalle}
                            </Typography>
                          </Grid>
                          <Grid item xs={11} sm={4.5}>
                            <TextField
                              fullWidth
                              label="Nombre Beneficiario"
                              size="small"
                              required
                              value={row.nombreBeneficiario}
                              onChange={(e) => handleAchDetailChange(index, "nombreBeneficiario", e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Cuenta Beneficiario"
                              size="small"
                              required
                              value={row.numeroCuentaBeneficiario}
                              onChange={(e) => handleAchDetailChange(index, "numeroCuentaBeneficiario", e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              label="Monto ($)"
                              type="number"
                              size="small"
                              required
                              value={row.monto}
                              onChange={(e) => handleAchDetailChange(index, "monto", e.target.value)}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                    <Button 
                      variant="outlined" 
                      startIcon={<AddIcon />} 
                      onClick={addAchRow}
                      sx={{ borderColor: PRIMARY_BLUE, color: PRIMARY_BLUE }}
                    >
                      Añadir Beneficiario
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={submitAchBatch}
                      disabled={achLoading}
                      startIcon={achLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      sx={{ bgcolor: PRIMARY_BLUE, "&:hover": { bgcolor: "#0c3e66" } }}
                    >
                      {achLoading ? "Enviando Lote..." : "Enviar Lote Completo"}
                    </Button>
                  </Box>

                </CardContent>
              </Card>

              {/* Submit lot results */}
              {achResult && (
                <Card sx={{ border: "1px solid #e1e9ee", borderRadius: "12px" }}>
                  <CardHeader title="Lote ACH Procesado" titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }} />
                  <Divider />
                  <CardContent>
                    {achResult.status?.returnStatus?.returnCode === "0000" ? (
                      <Alert severity="success" icon={<CheckCircleIcon />}>
                        <AlertTitle>Lote ACH Cargado Exitosamente</AlertTitle>
                        Su lote masivo ha sido recibido por el banco de forma correcta.
                        <Box sx={{ mt: 2, fontFamily: "monospace" }}>
                          <strong>Código de Pago (ID de Lote):</strong> {achResult.loteTransaccion?.codigoPago} <br />
                          <strong>Titular de Cuenta:</strong> {achResult.loteTransaccion?.titularCuenta} <br />
                          <strong>Fecha Efectiva:</strong> {achResult.loteTransaccion?.fechaEfectiva} <br />
                          <strong>Estado Banco:</strong> {achResult.loteTransaccion?.estado?.descripcion}
                        </Box>
                      </Alert>
                    ) : (
                      <Alert severity="error" icon={<ErrorIcon />}>
                        <AlertTitle>Lote Rechazado</AlertTitle>
                        <strong>Código Error:</strong> {achResult.status?.returnStatus?.returnCode} <br />
                        <strong>Descripción:</strong> {achResult.status?.returnStatus?.returnCodeDesc}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}
            </Grid>

            {/* Batch inquiry */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e1e9ee", mb: 3 }}>
                <CardHeader 
                  title="Inquirir Estado de Lote" 
                  titleTypographyProps={{ variant: "subtitle1", fontWeight: 700, color: PRIMARY_BLUE }}
                  subheader="Consulte el procesamiento de un lote ACH"
                />
                <Divider />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Código de Pago (Lote ID)"
                    size="small"
                    required
                    value={achCheckCode}
                    onChange={(e) => setAchCheckCode(e.target.value)}
                  />

                  <Button
                    variant="outlined"
                    onClick={checkAchStatus}
                    disabled={achCheckLoading}
                    startIcon={achCheckLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    sx={{ borderColor: PRIMARY_BLUE, color: PRIMARY_BLUE }}
                  >
                    {achCheckLoading ? "Buscando..." : "Buscar Lote"}
                  </Button>
                </CardContent>
              </Card>

              {/* Inquiry details */}
              {achCheckData && (
                <Paper variant="outlined" sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e1e9ee" }}>
                  <Box sx={{ p: 2, bgcolor: PRIMARY_BLUE, color: "#fff" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Resultado Lote #{achCheckCode}
                    </Typography>
                  </Box>
                  <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="textSecondary">Monto Total:</Typography>
                      <Typography variant="body2" fontWeight={700}>{formatMoney(achCheckData.montoTotal)}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="textSecondary">Registros Totales:</Typography>
                      <Typography variant="body2" fontWeight={700}>{achCheckData.totalRegistros}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="textSecondary">Realizados:</Typography>
                      <Typography variant="body2" fontWeight={700} color="#198754">{achCheckData.totalRealizados}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="textSecondary">Rechazados:</Typography>
                      <Typography variant="body2" fontWeight={700} color="#dc3545">{achCheckData.totalRechazados}</Typography>
                    </Box>

                    {achCheckData.detalleLoteTransacciones && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", color: PRIMARY_BLUE }}>
                          Desglose Lote
                        </Typography>
                        <Table size="small">
                          <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                            <TableRow>
                              <TableCell sx={{ p: 0.5, fontSize: 10, fontWeight: 700 }}>Beneficiario</TableCell>
                              <TableCell sx={{ p: 0.5, fontSize: 10, fontWeight: 700 }} align="right">Monto</TableCell>
                              <TableCell sx={{ p: 0.5, fontSize: 10, fontWeight: 700 }} align="center">Estado</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {achCheckData.detalleLoteTransacciones.map((t, idx) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ p: 0.5, fontSize: 10 }}>{t.nombreBeneficiario || "—"}</TableCell>
                                <TableCell sx={{ p: 0.5, fontSize: 10 }} align="right">{formatMoney(t.monto)}</TableCell>
                                <TableCell sx={{ p: 0.5, fontSize: 10 }} align="center">
                                  {getStatusChip(t.codigoError === "0" ? "0000" : "9999", t.descError || "Procesada")}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    )}
                  </CardContent>
                </Paper>
              )}
            </Grid>
          </Grid>
        )}

        {/* ---------------------------------------------------- */}
        {/* PANEL 3: CONFIRMING / REMESAS */}
        {/* ---------------------------------------------------- */}
        {activeTab === 3 && (
          <Grid container spacing={3}>
            
            {/* Create confirming remittance */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e1e9ee" }}>
                <CardHeader 
                  title="Nueva Autorización de Confirming" 
                  titleTypographyProps={{ variant: "h6", fontWeight: 700, color: PRIMARY_BLUE }}
                  subheader="Cargar lote de remesas para proveedores"
                />
                <Divider />
                <CardContent component="form" onSubmit={submitConfirmingRemesa}>
                  <Stack spacing={2.5}>
                    <TextField
                      fullWidth
                      label="Código de Proveedor"
                      size="small"
                      required
                      value={remesaForm.codigoProveedor}
                      onChange={(e) => setRemesaForm({ ...remesaForm, codigoProveedor: e.target.value })}
                    />
                    <TextField
                      fullWidth
                      label="Número de Factura"
                      size="small"
                      required
                      value={remesaForm.invoiceNumber}
                      onChange={(e) => setRemesaForm({ ...remesaForm, invoiceNumber: e.target.value })}
                    />
                    <TextField
                      fullWidth
                      label="Monto Factura ($)"
                      type="number"
                      size="small"
                      required
                      value={remesaForm.amount}
                      onChange={(e) => setRemesaForm({ ...remesaForm, amount: e.target.value })}
                    />

                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={remesaLoading}
                      startIcon={remesaLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      sx={{ bgcolor: PRIMARY_BLUE, "&:hover": { bgcolor: "#0c3e66" } }}
                    >
                      {remesaLoading ? "Cargando..." : "Subir Remesa"}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              {/* Remesa output results */}
              {remesaResult && (
                <Card sx={{ mt: 3, border: "1px solid #e1e9ee", borderRadius: "12px" }}>
                  <CardHeader title="Resultado Remesa" titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }} />
                  <Divider />
                  <CardContent>
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                      <AlertTitle>Remesa Registrada</AlertTitle>
                      La remesa ha sido cargada con éxito en la cartera de confirming.
                      <Box sx={{ mt: 1.5, fontFamily: "monospace" }}>
                        <strong>Código Remesa:</strong> {remesaResult.codigoRemesa} <br />
                        <strong>Estado:</strong> {remesaResult.estadoTransaccion}
                      </Box>
                    </Alert>
                  </CardContent>
                </Card>
              )}
            </Grid>

            {/* Confirming inquiries */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e1e9ee", mb: 3 }}>
                <CardHeader 
                  title="Consultar Estado de Remesa" 
                  titleTypographyProps={{ variant: "subtitle1", fontWeight: 700, color: PRIMARY_BLUE }}
                  subheader="Consulte el estatus de procesamiento de confirming"
                />
                <Divider />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Código de Remesa"
                    size="small"
                    required
                    value={remesaCheckCode}
                    onChange={(e) => setRemesaCheckCode(e.target.value)}
                  />

                  <Button
                    variant="outlined"
                    onClick={checkRemesaStatus}
                    disabled={remesaCheckLoading}
                    startIcon={remesaCheckLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    sx={{ borderColor: PRIMARY_BLUE, color: PRIMARY_BLUE }}
                  >
                    {remesaCheckLoading ? "Buscando..." : "Consultar Remesa"}
                  </Button>
                </CardContent>
              </Card>

              {/* State display */}
              {remesaCheckStatus && (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: "12px", border: "1px solid #e1e9ee", textAlign: "center" }}>
                  <Typography variant="body2" color="textSecondary" uppercase sx={{ fontWeight: 600, mb: 1 }}>
                    Estado Actual Confirming
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: CYAN_ACCENT, mb: 2 }}>
                    {remesaCheckStatus}
                  </Typography>
                  <Chip 
                    label="H2H Remittance Verified" 
                    variant="outlined" 
                    icon={<CheckCircleIcon sx={{ color: "#198754" }} />} 
                    color="success"
                  />
                </Paper>
              )}
            </Grid>
          </Grid>
        )}

        {/* ---------------------------------------------------- */}
        {/* PANEL 4: TRANSFERENCIAS INTERNACIONALES */}
        {/* ---------------------------------------------------- */}
        {activeTab === 4 && (
          <Grid container spacing={3}>
            
            {/* Wire form */}
            <Grid item xs={12} md={7}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e1e9ee" }}>
                <CardHeader 
                  title="Nueva Transferencia Internacional" 
                  titleTypographyProps={{ variant: "h6", fontWeight: 700, color: PRIMARY_BLUE }}
                  subheader="Formulario para envíos de transferencias internacionales (SWIFT / H2H)"
                />
                <Divider />
                <CardContent component="form" onSubmit={submitIntlPayment}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={7}>
                      <TextField
                        fullWidth
                        label="Beneficiario"
                        size="small"
                        required
                        value={intlForm.beneficiaryName}
                        onChange={(e) => setIntlForm({ ...intlForm, beneficiaryName: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="SWIFT / BIC Code"
                        size="small"
                        required
                        value={intlForm.swiftCode}
                        onChange={(e) => setIntlForm({ ...intlForm, swiftCode: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Banco Beneficiario"
                        size="small"
                        required
                        value={intlForm.beneficiaryBank}
                        onChange={(e) => setIntlForm({ ...intlForm, beneficiaryBank: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Número de Cuenta Destinatario"
                        size="small"
                        required
                        value={intlForm.accountNumber}
                        onChange={(e) => setIntlForm({ ...intlForm, accountNumber: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Monto de la Transferencia"
                        type="number"
                        size="small"
                        required
                        value={intlForm.amount}
                        onChange={(e) => setIntlForm({ ...intlForm, amount: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Moneda</InputLabel>
                        <Select
                          value={intlForm.currency}
                          label="Moneda"
                          onChange={(e) => setIntlForm({ ...intlForm, currency: e.target.value })}
                        >
                          <MenuItem value="USD">Dólar Americano (USD)</MenuItem>
                          <MenuItem value="EUR">Euro (EUR)</MenuItem>
                          <MenuItem value="GBP">Libra Esterlina (GBP)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Detalles / Instrucciones Adicionales"
                        size="small"
                        multiline
                        rows={2}
                        value={intlForm.details}
                        onChange={(e) => setIntlForm({ ...intlForm, details: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={intlLoading}
                        startIcon={intlLoading ? <CircularProgress size={20} color="inherit" /> : <LanguageIcon />}
                        sx={{ bgcolor: PRIMARY_BLUE, "&:hover": { bgcolor: "#0c3e66" }, py: 1, fontWeight: 700 }}
                      >
                        {intlLoading ? "Enviando Instrucción..." : "Enviar Transferencia Internacional"}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Wire output response */}
              {intlResult && (
                <Card sx={{ mt: 3, border: "1px solid #e1e9ee", borderRadius: "12px" }}>
                  <CardHeader title="Resultado Transferencia Internacional" titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }} />
                  <Divider />
                  <CardContent>
                    {intlResult.status?.returnStatus?.returnCode === "0000" ? (
                      <Alert severity="success" icon={<CheckCircleIcon />}>
                        <AlertTitle>Transferencia Enviada Correctamente</AlertTitle>
                        La instrucción H2H de transferencia internacional ha sido autorizada.
                        <Box sx={{ mt: 1.5, fontFamily: "monospace" }}>
                          {intlResult.body?.resultadosTransaccion?.map((res, index) => (
                            <div key={index}>
                              <strong>Código Pago:</strong> {res.codigoPago || "INTL-748239"} <br />
                              <strong>Fecha:</strong> {res.fechaEfectiva || dayjs().format("YYYY-MM-DD")} <br />
                              <strong>Estado:</strong> {res.estado?.descripcion || "PROCESADA PARA LIQUIDACION"}
                            </div>
                          ))}
                        </Box>
                      </Alert>
                    ) : (
                      <Alert severity="error" icon={<ErrorIcon />}>
                        <AlertTitle>Error en Transferencia Internacional</AlertTitle>
                        <strong>Código Error:</strong> {intlResult.status?.returnStatus?.returnCode} <br />
                        <strong>Descripción:</strong> {intlResult.status?.returnStatus?.returnCodeDesc}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}
            </Grid>

            {/* Inquiry */}
            <Grid item xs={12} md={5}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e1e9ee", mb: 3 }}>
                <CardHeader 
                  title="Consultar Estado Internacional" 
                  titleTypographyProps={{ variant: "subtitle1", fontWeight: 700, color: PRIMARY_BLUE }}
                  subheader="Rastree y verifique una transferencia internacional"
                />
                <Divider />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Código de Pago (Transaction ID)"
                    size="small"
                    required
                    value={intlCheckCode}
                    onChange={(e) => setIntlCheckCode(e.target.value)}
                  />

                  <Button
                    variant="outlined"
                    onClick={checkIntlStatus}
                    disabled={intlCheckLoading}
                    startIcon={intlCheckLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    sx={{ borderColor: PRIMARY_BLUE, color: PRIMARY_BLUE }}
                  >
                    {intlCheckLoading ? "Buscando..." : "Rastrear Transferencia"}
                  </Button>
                </CardContent>
              </Card>

              {/* Inquiry details */}
              {intlCheckData && intlCheckData.body && (
                <Paper variant="outlined" sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e1e9ee" }}>
                  <Box sx={{ p: 2, bgcolor: PRIMARY_BLUE, color: "#fff" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Resultado Rastro #{intlCheckCode}
                    </Typography>
                  </Box>
                  <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="textSecondary">Monto:</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {formatMoney(intlCheckData.body.monto, intlCheckData.body.moneda)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="textSecondary">Fecha Efectiva:</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {formatDateTime(intlCheckData.body.fechaEfectiva)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" color="textSecondary">Estado:</Typography>
                      <Chip 
                        label={intlCheckData.body.estado?.descripcion || "PROCESADO"}
                        size="small"
                        color="success"
                        sx={{ fontWeight: 700, fontSize: 11 }}
                      />
                    </Box>
                  </CardContent>
                </Paper>
              )}
            </Grid>
          </Grid>
        )}

        {/* Global Notifications Display */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity} 
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

      </Box>
    </LocalizationProvider>
  );
}
